import type { ItemResult } from '@creanizer/types'

interface ItemEffectsTabProps {
  effects: ItemResult['effects']
}

export function ItemEffectsTab({ effects }: ItemEffectsTabProps) {
  // 防御的チェック
  if (!effects) {
    return (
      <div className="text-muted-foreground">効果情報がありません</div>
    )
  }

  return (
    <>
      {effects.primaryEffect && (
        <div className="space-y-2">
          <h4 className="font-semibold">主要効果</h4>
          <div className="p-3 bg-muted/50 rounded-lg text-sm">
            <p>{effects.primaryEffect.description || '効果の説明がありません'}</p>
            <div className="mt-2 text-xs text-muted-foreground">
              {effects.primaryEffect.magnitude && `効果の強さ: ${effects.primaryEffect.magnitude}`}
              {effects.primaryEffect.duration && ` / 持続: ${effects.primaryEffect.duration}`}
            </div>
          </div>
        </div>
      )}

      {effects.secondaryEffects && effects.secondaryEffects.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">副次効果</h4>
          {effects.secondaryEffects.map((effect, idx) => (
            <div key={idx} className="p-2 bg-muted/30 rounded text-sm">
              {effect.description}
              {effect.condition && (
                <div className="text-xs text-muted-foreground mt-1">
                  条件: {effect.condition}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {effects.usageMethod && (
        <div className="space-y-2">
          <h4 className="font-semibold">使用方法</h4>
          <p className="text-sm">{effects.usageMethod}</p>
        </div>
      )}

      {effects.limitations && effects.limitations.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">制限事項</h4>
          <ul className="text-sm space-y-1">
            {effects.limitations.map((limit, idx) => (
              <li key={idx}>・{limit}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

interface ItemAcquisitionTabProps {
  acquisition: ItemResult['acquisition']
}

export function ItemAcquisitionTab({ acquisition }: ItemAcquisitionTabProps) {
  // 防御的チェック
  if (!acquisition) {
    return (
      <div className="text-muted-foreground">入手情報がありません</div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="font-medium">推定価値:</span> {acquisition.estimatedValue || '不明'}
        </div>
        <div>
          <span className="font-medium">レア度:</span> {acquisition.rarity}
        </div>
        {acquisition.demandLevel && (
          <div>
            <span className="font-medium">需要レベル:</span> {acquisition.demandLevel}
          </div>
        )}
        {acquisition.availability && (
          <div>
            <span className="font-medium">入手可能性:</span> {acquisition.availability}
          </div>
        )}
      </div>
      
      {acquisition.valueFluctuation && (
        <div className="p-3 bg-muted/30 rounded text-sm">
          <div className="font-medium mb-1">価値の変動</div>
          <p>{acquisition.valueFluctuation}</p>
        </div>
      )}
      
      {acquisition.obtainMethod && acquisition.obtainMethod.length > 0 && (
        <div>
          <h4 className="font-semibold mb-2">入手方法</h4>
          <ul className="text-sm space-y-1">
            {acquisition.obtainMethod.map((method, idx) => (
              <li key={idx}>・{method}</li>
            ))}
          </ul>
        </div>
      )}

      {acquisition.locations && acquisition.locations.length > 0 && (
        <div>
          <h4 className="font-semibold mb-2">入手場所</h4>
          <ul className="text-sm space-y-1">
            {acquisition.locations.map((location, idx) => (
              <li key={idx}>・{location}</li>
            ))}
          </ul>
        </div>
      )}

      {acquisition.dropRate && (
        <div className="text-sm">
          <span className="font-medium">ドロップ率:</span> {acquisition.dropRate}
        </div>
      )}
    </>
  )
}

interface ItemLoreTabProps {
  lore: ItemResult['lore']
}

export function ItemLoreTab({ lore }: ItemLoreTabProps) {
  // 防御的チェック
  if (!lore) {
    return (
      <div className="text-muted-foreground">伝承情報がありません</div>
    )
  }

  return (
    <>
      {lore.originStory && (
        <div className="space-y-2">
          <h4 className="font-semibold">起源物語</h4>
          <div className="p-3 bg-muted/30 rounded">
            <p className="text-sm mb-2">{lore.originStory.narrative || '詳細不明'}</p>
            <div className="text-xs text-muted-foreground space-y-1">
              {lore.originStory.era && <div>時代: {lore.originStory.era}</div>}
              {lore.originStory.location && <div>場所: {lore.originStory.location}</div>}
              {lore.originStory.creator && <div>創造者: {lore.originStory.creator}</div>}
              {lore.originStory.purpose && <div>目的: {lore.originStory.purpose}</div>}
              {lore.originStory.firstOwner && <div>最初の所有者: {lore.originStory.firstOwner}</div>}
              {lore.originStory.creationMethod && <div>製作方法: {lore.originStory.creationMethod}</div>}
            </div>
          </div>
        </div>
      )}

      {lore.legends && lore.legends.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">伝説</h4>
          {lore.legends.map((legend, idx) => (
            <div key={idx} className="p-3 bg-muted/30 rounded">
              <div className="font-medium text-sm mb-1">{legend.title}</div>
              <p className="text-sm mb-2">{legend.story}</p>
              <div className="text-xs text-muted-foreground">
                {legend.period} / {legend.region} / 信憑性: {legend.reliability}
                {legend.moral && <div className="mt-1">教訓: {legend.moral}</div>}
                {legend.impact && <div>影響: {legend.impact}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {lore.famousOwners && lore.famousOwners.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">著名な所有者</h4>
          {lore.famousOwners.map((owner, idx) => (
            <div key={idx} className="p-3 bg-muted/30 rounded">
              <div className="font-medium text-sm">
                {owner.name}
                {owner.title && ` - ${owner.title}`}
              </div>
              <div className="text-xs text-muted-foreground mb-1">
                {owner.era} / 所有期間: {owner.possession}
              </div>
              <p className="text-sm mb-1">{owner.deeds}</p>
              <div className="text-xs text-muted-foreground">
                運命: {owner.fate}
                {owner.legacy && <div>影響: {owner.legacy}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {lore.incidents && lore.incidents.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">重要な事件</h4>
          {lore.incidents.map((incident, idx) => (
            <div key={idx} className="p-3 bg-muted/30 rounded">
              <div className="text-xs text-muted-foreground mb-1">
                {incident.date} - {incident.location}
              </div>
              <p className="text-sm mb-1">{incident.description}</p>
              <div className="text-xs text-muted-foreground">
                {incident.involved && incident.involved.length > 0 && <div>関係者: {incident.involved.join(', ')}</div>}
                {incident.outcome && <div>結果: {incident.outcome}</div>}
                {incident.significance && <div>意義: {incident.significance}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {lore.rumors && lore.rumors.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">噂と伝承</h4>
          {lore.rumors.map((rumor, idx) => (
            <div key={idx} className="p-2 bg-muted/30 rounded">
              <p className="text-sm">{rumor.content}</p>
              <div className="text-xs text-muted-foreground mt-1">
                出所: {rumor.source} / 信憑性: {rumor.believability}
                {rumor.spread && <div>広まり方: {rumor.spread}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {lore.culturalImpact && (
        <div className="space-y-2">
          <h4 className="font-semibold">文化的影響</h4>
          <p className="text-sm">{lore.culturalImpact}</p>
        </div>
      )}

      {lore.symbolism && (
        <div className="space-y-2">
          <h4 className="font-semibold">象徴的意味</h4>
          <p className="text-sm">{lore.symbolism}</p>
        </div>
      )}

      {lore.curses && lore.curses.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">呪い</h4>
          {lore.curses.map((curse, idx) => (
            <div key={idx} className="p-2 bg-red-950/20 rounded">
              <p className="text-sm">{curse.description}</p>
              <div className="text-xs text-muted-foreground mt-1">
                発動条件: {curse.trigger} / 効果: {curse.effect}
                {curse.breakingMethod && <div>解除方法: {curse.breakingMethod}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {lore.prophecies && lore.prophecies.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">予言</h4>
          {lore.prophecies.map((prophecy, idx) => (
            <div key={idx} className="p-2 bg-purple-950/20 rounded">
              <p className="text-sm italic">"{prophecy.text}"</p>
              <div className="text-xs text-muted-foreground mt-1">
                出典: {prophecy.source}
                {prophecy.interpretation && <div>解釈: {prophecy.interpretation}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {lore.flavorText && (
        <div className="p-3 bg-muted/50 rounded-lg italic text-sm">
          "{lore.flavorText}"
        </div>
      )}

      {lore.quotes && lore.quotes.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-semibold">引用</h4>
          {lore.quotes.map((quote, idx) => (
            <div key={idx} className="p-2 bg-muted/30 rounded">
              <p className="text-sm italic">"{quote.text}"</p>
              <div className="text-xs text-muted-foreground mt-1">
                - {quote.speaker}
                {quote.context && ` (${quote.context})`}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}